import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Stack,
  Divider,
  CircularProgress,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import AssessmentIcon from "@mui/icons-material/Assessment";
import DateRangePicker, { DateRangeValue } from "../Components/Reports/DateRangePicker";
import SalesReports from "../Components/Reports/SalesReports";
import MarketingReports from "../Components/Reports/MarketingReports";
import SupportReports from "../Components/Reports/SupportReports";
import ExportFormatSelector, { ExportFormat } from "../Components/Reports/ExportFormatSelector";
import {
  exportSalesReport,
  exportMarketingReport,
  exportSupportReport,
} from "../Services/reportsService";
import { exportMultipleSheetsToExcel, exportToPDF } from "../Utils/exportUtils";
import SalesPDFReport from "../Components/Reports/PDFTemplates/SalesPDFReport";
import MarketingPDFReport from "../Components/Reports/PDFTemplates/MarketingPDFReport";
import SupportPDFReport from "../Components/Reports/PDFTemplates/SupportPDFReport";
import { Vocabulary } from "../Utils/Vocabulary";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reports-tabpanel-${index}`}
      aria-labelledby={`reports-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `reports-tab-${index}`,
    "aria-controls": `reports-tabpanel-${index}`,
  };
}

const Reports: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [dateRange, setDateRange] = useState<DateRangeValue>({
    preset: "LAST_30_DAYS",
    groupBy: "day",
  });
  const [exportFormat, setExportFormat] = useState<ExportFormat>('csv');
  const [isExporting, setIsExporting] = useState(false);

  // Store data from child components for export
  const [salesData, setSalesData] = useState<any>(null);
  const [marketingData, setMarketingData] = useState<any>(null);
  const [supportData, setSupportData] = useState<any>(null);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleDateRangeChange = (newDateRange: DateRangeValue) => {
    setDateRange(newDateRange);
  };

  const handleExportFormatChange = (format: ExportFormat) => {
    setExportFormat(format);
  };

  const formatDateRange = () => {
    if (dateRange.startDate && dateRange.endDate) {
      const start = new Date(dateRange.startDate).toLocaleDateString('ro-RO');
      const end = new Date(dateRange.endDate).toLocaleDateString('ro-RO');
      return `${start} - ${end}`;
    }
    return dateRange.preset?.replace(/_/g, ' ') || 'All Time';
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      if (exportFormat === 'csv') {
        // Use existing backend CSV export
        switch (tabValue) {
          case 0:
            exportSalesReport(dateRange);
            break;
          case 1:
            exportMarketingReport(dateRange);
            break;
          case 2:
            exportSupportReport(dateRange);
            break;
        }
      } else {
        // Frontend Excel/PDF export
        switch (tabValue) {
          case 0:
            await exportSalesData();
            break;
          case 1:
            await exportMarketingData();
            break;
          case 2:
            await exportSupportData();
            break;
        }
      }
    } catch (error) {
      console.error('Export error:', error);
      alert(Vocabulary.errorGeneratingReport);
    } finally {
      setIsExporting(false);
    }
  };

  const exportSalesData = async () => {
    if (!salesData) {
      alert(Vocabulary.noDataAvailable);
      return;
    }

    const filename = `sales-report-${Date.now()}`;

    if (exportFormat === 'excel') {
      const sheets = [];

      if (salesData.revenueTrends?.length > 0) {
        sheets.push({
          data: salesData.revenueTrends,
          sheetName: 'Revenue Trends'
        });
      }

      if (salesData.ordersByStatus?.length > 0) {
        sheets.push({
          data: salesData.ordersByStatus,
          sheetName: 'Orders by Status'
        });
      }

      if (salesData.topProducts?.length > 0) {
        sheets.push({
          data: salesData.topProducts,
          sheetName: 'Top Products'
        });
      }

      if (salesData.paymentMethods?.length > 0) {
        sheets.push({
          data: salesData.paymentMethods,
          sheetName: 'Payment Methods'
        });
      }

      if (salesData.topClients?.length > 0) {
        sheets.push({
          data: salesData.topClients,
          sheetName: 'Top Clients'
        });
      }

      exportMultipleSheetsToExcel(sheets, filename);
    } else if (exportFormat === 'pdf') {
      const pdfData = {
        dateRange: formatDateRange(),
        kpis: salesData.kpis,
        revenueTrends: salesData.revenueTrends,
        ordersByStatus: salesData.ordersByStatus,
        topProducts: salesData.topProducts,
        paymentMethods: salesData.paymentMethods,
        topClients: salesData.topClients,
      };
      await exportToPDF(<SalesPDFReport data={pdfData} />, filename);
    }
  };

  const exportMarketingData = async () => {
    if (!marketingData) {
      alert(Vocabulary.noDataAvailable);
      return;
    }

    const filename = `marketing-report-${Date.now()}`;

    if (exportFormat === 'excel') {
      const sheets = [];

      if (marketingData.leadConversion?.length > 0) {
        sheets.push({
          data: marketingData.leadConversion,
          sheetName: 'Lead Conversion'
        });
      }

      if (marketingData.campaignPerformance?.length > 0) {
        sheets.push({
          data: marketingData.campaignPerformance,
          sheetName: 'Campaign Performance'
        });
      }

      if (marketingData.clientSegmentationByCategory?.length > 0) {
        sheets.push({
          data: marketingData.clientSegmentationByCategory,
          sheetName: 'Clients by Category'
        });
      }

      if (marketingData.clientSegmentationByPeriodicity?.length > 0) {
        sheets.push({
          data: marketingData.clientSegmentationByPeriodicity,
          sheetName: 'Clients by Periodicity'
        });
      }

      if (marketingData.agentPerformance?.length > 0) {
        sheets.push({
          data: marketingData.agentPerformance,
          sheetName: 'Agent Performance'
        });
      }

      if (marketingData.agentConversionRate?.length > 0) {
        sheets.push({
          data: marketingData.agentConversionRate,
          sheetName: 'Agent Conversion Rate'
        });
      }

      exportMultipleSheetsToExcel(sheets, filename);
    } else if (exportFormat === 'pdf') {
      const pdfData = {
        dateRange: formatDateRange(),
        kpis: marketingData.kpis,
        leadConversion: marketingData.leadConversion,
        campaignPerformance: marketingData.campaignPerformance,
        clientSegmentationByCategory: marketingData.clientSegmentationByCategory,
        clientSegmentationByPeriodicity: marketingData.clientSegmentationByPeriodicity,
        agentPerformance: marketingData.agentPerformance,
        agentConversionRate: marketingData.agentConversionRate,
      };
      await exportToPDF(<MarketingPDFReport data={pdfData} />, filename);
    }
  };

  const exportSupportData = async () => {
    if (!supportData) {
      alert(Vocabulary.noDataAvailable);
      return;
    }

    const filename = `support-report-${Date.now()}`;

    if (exportFormat === 'excel') {
      const sheets = [];

      if (supportData.tasksByStatus?.length > 0) {
        sheets.push({
          data: supportData.tasksByStatus,
          sheetName: 'Tasks by Status'
        });
      }

      if (supportData.tasksByPriority?.length > 0) {
        sheets.push({
          data: supportData.tasksByPriority,
          sheetName: 'Tasks by Priority'
        });
      }

      if (supportData.ticketsByStatus?.length > 0) {
        sheets.push({
          data: supportData.ticketsByStatus,
          sheetName: 'Tickets by Status'
        });
      }

      if (supportData.ticketsByType?.length > 0) {
        sheets.push({
          data: supportData.ticketsByType,
          sheetName: 'Tickets by Type'
        });
      }

      if (supportData.ticketVolume?.length > 0) {
        sheets.push({
          data: supportData.ticketVolume,
          sheetName: 'Ticket Volume'
        });
      }

      if (supportData.responseTimesByType?.length > 0) {
        sheets.push({
          data: supportData.responseTimesByType,
          sheetName: 'Response Times'
        });
      }

      if (supportData.agentWorkload?.length > 0) {
        sheets.push({
          data: supportData.agentWorkload,
          sheetName: 'Agent Workload'
        });
      }

      if (supportData.agentCompletionRate?.length > 0) {
        sheets.push({
          data: supportData.agentCompletionRate,
          sheetName: 'Agent Completion Rate'
        });
      }

      exportMultipleSheetsToExcel(sheets, filename);
    } else if (exportFormat === 'pdf') {
      const pdfData = {
        dateRange: formatDateRange(),
        kpis: supportData.kpis,
        tasksByStatus: supportData.tasksByStatus,
        tasksByPriority: supportData.tasksByPriority,
        ticketsByStatus: supportData.ticketsByStatus,
        ticketsByType: supportData.ticketsByType,
        ticketVolume: supportData.ticketVolume,
        responseTimesByType: supportData.responseTimesByType,
        agentWorkload: supportData.agentWorkload,
        agentCompletionRate: supportData.agentCompletionRate,
      };
      await exportToPDF(<SupportPDFReport data={pdfData} />, filename);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <AssessmentIcon sx={{ fontSize: 40, color: "primary.main" }} />
          <Typography variant="h4" component="h1" fontWeight="bold">
            {Vocabulary.reportsAndAnalytics}
          </Typography>
        </Stack>
        <Typography variant="body1" color="text.secondary">
          Generați rapoarte detaliate și analizați performanța pentru vânzări, marketing și suport.
        </Typography>
      </Box>

      {/* Date Range Picker and Export Controls */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          spacing={2}
        >
          <DateRangePicker
            onDateRangeChange={handleDateRangeChange}
            showGroupBy={tabValue === 0} // Show groupBy only for Sales (revenue trends)
          />
          <Stack direction="row" spacing={2} alignItems="center">
            <ExportFormatSelector
              value={exportFormat}
              onChange={handleExportFormatChange}
              disabled={isExporting}
            />
            <Button
              variant="contained"
              color="secondary"
              startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              onClick={handleExport}
              disabled={isExporting}
              sx={{ minWidth: 150 }}
            >
              {isExporting ? Vocabulary.generatingReport : Vocabulary.downloadReport}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Tabs */}
      <Paper elevation={3}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="reports tabs"
            variant="fullWidth"
            sx={{
              "& .MuiTab-root": {
                minHeight: 64,
                fontSize: "1rem",
                fontWeight: "bold",
              },
            }}
          >
            <Tab label={Vocabulary.salesReports} {...a11yProps(0)} />
            <Tab label={Vocabulary.marketingReports} {...a11yProps(1)} />
            <Tab label={Vocabulary.supportReports} {...a11yProps(2)} />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <Box sx={{ p: 3 }}>
          <TabPanel value={tabValue} index={0}>
            <SalesReports dateRange={dateRange} onDataChange={setSalesData} />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <MarketingReports dateRange={dateRange} onDataChange={setMarketingData} />
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <SupportReports dateRange={dateRange} onDataChange={setSupportData} />
          </TabPanel>
        </Box>
      </Paper>

      {/* Footer Info */}
      <Box sx={{ mt: 3 }}>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary" align="center">
          {Vocabulary.reports} • Ultima actualizare: {new Date().toLocaleString("ro-RO")}
        </Typography>
      </Box>
    </Container>
  );
};

export default Reports;
